import { Item } from './item';

export interface CartItem
  extends Omit<
    Item,
    | 'quantity'
    | 'rarity_id'
    | 'fiat_price'
    | 'fg_price'
    | 'image_path'
    | 'stats_description'
    | 'status'
    | 'suffix'
  > {
  quantity: number;
  metadata?: any;
  carted_at?: string;
  // Override optional fields that are required in cart context
  fiat_price: string;
  fg_price: string;
  image_path: string;
  stats_description: string;
  status: string;
  suffix: string;
  // Override type mismatches
  rarity_id?: string;
}

export interface CartItemGroup {
  key: string;
  items: CartItem[];
  quantity: number;
  totalFiatPrice: number;
  totalFgPrice: number;
}

export interface CartSummary {
  totalItems: number;
  totalFiatPrice: string;
  totalFgPrice: string;
  currency: 'fiat' | 'fg';
}

export interface CartOrderRequest {
  game_name: string;
  game_password: string;
  realm: 'europe' | 'americas' | 'asia';
  currency: 'fiat' | 'fg';
  item_ids: number[];
  is_halted: number;
}

export interface CartMetadata {
  [key: string]: any;
}

// Utility function to create cart item grouping key
export const createCartItemGroupKey = (item: CartItem): string => {
  return [
    item.stats_description || '',
    item.ocr_string || '',
    item.rarity_id || '',
    item.ladder || '',
    item.hardcore || '',
    item.fiat_price || '',
    item.fg_price || '',
  ].join('|');
};

// Utility function to group cart items
export const groupCartItems = (items: CartItem[]): Record<string, CartItemGroup> => {
  const groups: Record<string, CartItemGroup> = {};

  for (const item of items) {
    const key = createCartItemGroupKey(item);

    if (!groups[key]) {
      groups[key] = {
        key,
        items: [],
        quantity: 0,
        totalFiatPrice: 0,
        totalFgPrice: 0,
      };
    }

    groups[key].items.push(item);
    groups[key].quantity += 1;

    const fiatPrice = parseFloat(item.fiat_price || '0');
    const fgPrice = parseFloat(item.fg_price || '0');

    groups[key].totalFiatPrice += fiatPrice;
    groups[key].totalFgPrice += fgPrice;
  }

  return groups;
};

// Utility function to calculate cart summary
export const calculateCartSummary = (
  items: CartItem[],
  currency: 'fiat' | 'fg' = 'fiat'
): CartSummary => {
  const totalItems = items.length;
  let totalFiat = 0;
  let totalFg = 0;

  for (const item of items) {
    totalFiat += parseFloat(item.fiat_price || '0');
    totalFg += parseFloat(item.fg_price || '0');
  }

  return {
    totalItems,
    totalFiatPrice: totalFiat.toFixed(2),
    totalFgPrice: totalFg.toFixed(2),
    currency,
  };
};
